import { FocusMonitor } from '@angular/cdk/a11y';
import { Component, ElementRef, Inject, NgZone, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormField } from '@angular/material/form-field';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs/internal/Observable';
import { map, startWith } from 'rxjs/operators';
import { Catch } from '../shared/catch.model';
import { CommonFunctionService } from '../shared/common-function-service.service';
import { LocalStorageService } from '../shared/local-storage.service';
import { LookupDataService } from '../shared/lookup-data.service';
import { LookupItem } from '../shared/lookup-item.model';
import { speciesValidator } from '../shared/trip-validator.model';


/**
 * Dialog to select catch species, either typed from an autocomplete, selected from a previous species, or selected from favorite species.
 * Two screens here, first to select the species, second to enter its weights.
 */

@Component({
    selector: 'app-add-catch-hms-dialog',
    templateUrl: './add-catch-hms-dialog.component.html',
    styleUrl: './add-catch-hms-dialog.component.css',
    standalone: false
})
export class AddCatchHmsDialogComponent {

  @ViewChild('speciesInput') speciesInput!: ElementRef;

  catchFormGroup!: FormGroup;

  favoriteSpecies: LookupItem[] = [];
  speciesList: LookupItem[] = [];
  filteredSpecies!: Observable<LookupItem[]>;
  previousSpecies: LookupItem[] = [];

  speciesName: string | null = '';
  weightsSection: boolean = false;


  constructor(
    public router: Router,
    public dialogRef: MatDialogRef<AddCatchHmsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {item: Catch, isHarpoon: boolean},
    public focusMonitor: FocusMonitor,
    public ngZone: NgZone,
    private toastr: ToastrService,
    private lookup: LookupDataService,
    private common: CommonFunctionService
  ) { }

  /**
   * Initialization -- Set the catch form and class variables. Go to the weight section if an existing species is being edited.
   */
  ngOnInit(): void {

    this.catchFormGroup = new FormGroup({
      species: new FormControl('', [Validators.required, speciesValidator()]),
      retainedCount: new FormControl(0, [Validators.min(0), Validators.max(3000)]),
      retainedWeight: new FormControl(0, Validators.min(0)),
      releasedCount: new FormControl(0, [Validators.min(0), Validators.max(3000)]),
      releasedWeight: new FormControl(0, Validators.min(0))
    });

    this.catchFormGroup.patchValue({
      species: this.data.item.species,
      retainedCount: this.data.item.retainedCount,
      retainedWeight: this.data.item.retainedWeight,
      releasedCount: this.data.item.releasedCount,
      releasedWeight: this.data.item.releasedWeight
    });

    this.speciesName = this.data.item.species.name;

    // species already selected
    this.weightsSection = this.speciesName != '';

    // species not selected yet
    if (!this.weightsSection) {
      this.catchFormGroup.get('species')!.valueChanges.subscribe(val => {
        this.speciesName = val!;
      });

    }

    this.speciesList = this.lookup.getHmsSetSpecies();
    this.filteredSpecies = this.catchFormGroup.get('species')!.valueChanges.pipe(
      startWith(''),
      map((value: any) => this.speciesList.filter(option => option.name.toLowerCase().includes(value.toLowerCase())))
    );

    // get favorite species list
    this.favoriteSpecies = LocalStorageService.current_user.favoriteSpecies.filter(sp => sp.lookupType == 'HMSSPECIES');

    // get species from previous sets
    LocalStorageService.user_trip.sets.filter(s => s.fishery == 'HMS').forEach(set => {
      set.catchItems.forEach(ci => {
        if (ci.species.name != '' && !this.previousSpecies.find(x => x.name == ci.species.name) && !this.favoriteSpecies.find(x => x.name == ci.species.name)) {
          this.previousSpecies.push(ci.species);
        }
      });
    });

  }

    /**
   * Adding a new favorite species done as in the Settings component
   */
    addSpecies(): void {
      this.common.addSpecies(-1, "HMS").finally(() => this.favoriteSpecies = LocalStorageService.current_user.favoriteSpecies.filter(sp => sp.lookupType == 'HMSSPECIES'))
    }

  /**
   * Quick selection from the previous species list
   * @param item 
   */
  quickSelect(item: LookupItem) {
    this.catchFormGroup.get('species')?.setValue(item);
    this.confirmSpecies();
  }


  /**
   * After confirming a valid species in the catch form
   */
  confirmSpecies() {
    const speciesControl = this.catchFormGroup.get('species');
    speciesControl?.markAsTouched();

    if (speciesControl?.valid) {
      this.weightsSection = true;
    }
  }


  /**
   * Check for valid catch form and weights, then close the dialog with result
   * @returns 
   */
  confirmCatch() {
    if (this.catchFormGroup.invalid || !this.weightsSection) {
      this.toastr.error("Pieces and weight must both be filled", "Invalid form");
      return;
    }
    this.dialogRef.close(this.catchFormGroup.value);
  }

  /**
   * If enter key pressed
   * @param key Key pressed
   */
  handleKeyUp(key: any) {
    if (key.keyCode == 13) this.confirmCatch();
  }


  onNoClick(): void {
    this.dialogRef.close();
  }

  public removeFocus(field: MatFormField, input: HTMLInputElement) {
    setTimeout(() => {
      input.blur();
    }, 50);
  }


  _forbidNonNumeric(control: AbstractControl) {
    control.setValue(control.value.replace(/[^0-9]/g, ''), { onlySelf: true, emitEvent: false });
  }



}

